import { FunctionComponent, memo, useContext, useMemo } from "react";
import { SpinnerUntil } from "@edgetier/components";
import { InteractionType, MessageDirection } from "@edgetier/watchtower-types";
import { compareAsc, format } from "date-fns";
import InteractionMessageContent from "./interaction-message/interaction-message-content";
import { IProps } from "./interaction-messages.types";
import {
    groupWritingIssuesByMessage,
    useInteractionMessages,
    useInteractionMessagesWritingIssues,
} from "./interaction-messages.utilities";
import InteractionMessage from "./interaction-message";
import { Box } from "@edgetier/client-components";
import { InteractionContext } from "components-for/interactions/interaction-context/interaction-context";
import DateIndicator from "./interaction-message/date-indicator";
import { groupBy } from "@edgetier/utilities";
import { useInteractionDetails } from "hooks-for/interactions/use-interaction-details";
import { useMessagesAgents } from "hooks-for/interaction-messages/use-messages-agents";
import usePhraseMessageTranslation from "hooks-for/translations/use-phrase-message-translation";
import "./interaction-messages.scss";

/**
 * Show the message for an interaction that the user has selected.
 * @param props.interactionId   The ID of the interaction being displayed.
 * @param props.interactionType The type of interaction being displayed.
 * @param props.highlightedMessageId
 */
const InteractionMessages: FunctionComponent<IProps> = ({ highlightedMessageId, interactionId, interactionType }) => {
    const { areWritingIssuesVisible } = useContext(InteractionContext);
    const { data: interactionMessages, isLoading: areMessagesLoading } = useInteractionMessages(interactionId, {
        select: (messages) => messages.sort((one, two) => compareAsc(one.sendDateTime, two.sendDateTime)),
    });

    // Get interaction details for customer name
    const { data: interactionDetails, isLoading: areInteractionDetailsLoading } = useInteractionDetails(interactionId);

    const { activeTranslations, toggleTranslation } = usePhraseMessageTranslation();

    const messagesGroupedByDate = useMemo(
        () => groupBy(interactionMessages ?? [], ({ sendDateTime }) => format(sendDateTime, "cccc, do LLLL yyyy")),
        [interactionMessages]
    );

    // Find all agents involved in the interaction messages
    const agentIdsFromMessages = useMemo(
        () =>
            interactionMessages?.reduce<string[]>((agentIds, message) => {
                // Select agentIds only once
                if (typeof message.agentId === "string" && !agentIds.includes(message.agentId)) {
                    return [...agentIds, message.agentId];
                }
                return agentIds;
            }, []),
        [interactionMessages]
    );

    const messagesAgents = useMessagesAgents(agentIdsFromMessages ?? []);
    const areMessagesAgentsLoading = useMemo(() => messagesAgents.some(({ isLoading }) => isLoading), [messagesAgents]);

    // Names for all agents involved in the interaction
    const messagesAgentsNames = useMemo(
        () =>
            messagesAgents.reduce<Record<string, string>>((agentNames, messageAgentRequest) => {
                const { data: agentDetails } = messageAgentRequest;
                if (typeof agentDetails !== "undefined") {
                    const agentId = agentDetails.agentId;

                    // Return only name of an agent
                    return {
                        ...agentNames,
                        [agentId]: agentDetails.name,
                    };
                }
                return {
                    ...agentNames,
                };
            }, {}),
        [messagesAgents]
    );

    const { data: writingIssues, isLoading: areWritingIssuesLoading } = useInteractionMessagesWritingIssues(
        interactionId,
        {
            enabled: areWritingIssuesVisible,
            select: (writingIssues) => writingIssues.reduce(groupWritingIssuesByMessage, {}),
        }
    );
    return (
        <SpinnerUntil
            isReady={
                !areMessagesLoading &&
                !areWritingIssuesLoading &&
                !areInteractionDetailsLoading &&
                !areMessagesAgentsLoading
            }
            data={interactionMessages}
        >
            <div className="interaction-messages">
                {typeof interactionMessages === "undefined" ? (
                    <div>Error</div>
                ) : interactionType === InteractionType.Email && interactionMessages.length === 1 ? (
                    <Box>
                        <InteractionMessageContent
                            interactionType={interactionType}
                            ignoreDirection
                            message={interactionMessages[0]}
                            activeTranslation={activeTranslations[interactionMessages[0].interactionMessageId]}
                            toggleTranslation={toggleTranslation}
                            writingIssues={writingIssues?.[interactionMessages[0].interactionMessageId] ?? []}
                        />
                    </Box>
                ) : (
                    // Grouping the messages by date means the date indicator will stick to the top of each group.
                    Object.entries(messagesGroupedByDate).map(([date, messages]) => (
                        <div className="interaction-messages__date-group" key={date}>
                            <DateIndicator date={date} />
                            {messages.map((message) => (
                                <InteractionMessage
                                    key={message.interactionMessageId}
                                    message={message}
                                    senderDetails={
                                        message.direction === MessageDirection.Inbound
                                            ? interactionDetails?.senderName
                                            : typeof message.agentId === "string"
                                            ? messagesAgentsNames?.[message.agentId]
                                            : null
                                    }
                                    interactionType={interactionType}
                                    isMessageHighlighted={message.interactionMessageId === highlightedMessageId}
                                    writingIssues={writingIssues?.[message.interactionMessageId] ?? []}
                                    activeTranslation={activeTranslations[message.interactionMessageId]}
                                    toggleTranslation={toggleTranslation}
                                />
                            ))}
                        </div>
                    ))
                )}
            </div>
        </SpinnerUntil>
    );
};

export default memo(InteractionMessages);
